/**
 * Pure route-geometry logic for drawing a courier's active order on the tracking map.
 *
 * Mirrors the active-point rule used across the order-single page
 * (`getRouteTimerProps` in features/orders/model/helpers): the "active" point is the
 * first route stop the courier has NOT finished — either not arrived at yet, or arrived
 * but not handed over. Everything before it is completed; everything after is upcoming.
 *
 * This module is intentionally free of React / ymaps so it can be characterization-tested.
 */

export interface RoutePointLocation {
  lat?: number | null | undefined;
  long?: number | null | undefined;
}

export interface RawRoutePoint {
  location?: RoutePointLocation | null | undefined;
  address?: string | null | undefined;
  arrived_at?: string | null | undefined;
  handed_over_at?: string | null | undefined;
  initial_eta?: string | null | undefined;
  eta?: string | null | undefined;
}

export interface ActiveOrderLike {
  id?: string | number | undefined;
  display_id?: string | number | null | undefined;
  stage?: number | undefined;
  origin?: RawRoutePoint[] | null | undefined;
  destination?: RawRoutePoint[] | null | undefined;
}

export type RoutePointType = 'origin' | 'destination';

export interface RoutePoint {
  /** [long, lat] — YMaps3 coordinate order */
  coords: [number, number];
  type: RoutePointType;
  /** 0-based index WITHIN its own type group (origin vs destination), for placemark numbering */
  typeIndex: number;
  originLength: number;
  destinationLength: number;
  address?: string | null | undefined;
  arrived_at?: string | null | undefined;
  handed_over_at?: string | null | undefined;
  initial_eta?: string | null | undefined;
  eta?: string | null | undefined;
}

export interface RouteLeg {
  /** [ [long,lat], [long,lat] ] straight endpoints of this leg */
  from: [number, number];
  to: [number, number];
  /** completed = courier already finished the `to` point; active = the leg currently being travelled; upcoming = not yet reached */
  state: 'completed' | 'active' | 'upcoming';
}

const isFiniteCoord = (lng: unknown, lat: unknown): boolean =>
  Number.isFinite(lng) && Number.isFinite(lat);

/**
 * Flatten an active order's origin + destination stops (in travel order) into typed,
 * coordinate-validated route points. Points with missing/invalid coords are dropped.
 */
export function getOrderRoutePoints(
  order: ActiveOrderLike | null | undefined,
): RoutePoint[] {
  if (!order) return [];

  const origins = (order.origin ?? []).filter((p) =>
    isFiniteCoord(p?.location?.long, p?.location?.lat),
  );
  const destinations = (order.destination ?? []).filter((p) =>
    isFiniteCoord(p?.location?.long, p?.location?.lat),
  );

  const originLength = origins.length;
  const destinationLength = destinations.length;

  const toPoint =
    (type: RoutePointType) =>
    (p: RawRoutePoint, i: number): RoutePoint => ({
      coords: [p.location!.long!, p.location!.lat!],
      type,
      typeIndex: i,
      originLength,
      destinationLength,
      address: p.address ?? null,
      arrived_at: p.arrived_at ?? null,
      handed_over_at: p.handed_over_at ?? null,
      initial_eta: p.initial_eta ?? null,
      eta: p.eta ?? null,
    });

  return [...origins.map(toPoint('origin')), ...destinations.map(toPoint('destination'))];
}

/**
 * Index (within the flattened points array) of the point the courier is currently
 * heading to / waiting at. Returns the last point if every stop is finished, and -1 for
 * an empty route. Same selection rule as order-single's `getRouteTimerProps`.
 */
export function getActivePointIndex(points: RoutePoint[]): number {
  if (!points.length) return -1;
  const idx = points.findIndex((p) => !p.arrived_at || !p.handed_over_at);
  return idx === -1 ? points.length - 1 : idx;
}

/**
 * Break the flattened route into consecutive legs, each tagged by its completion state
 * relative to the courier's progress. A leg is `completed` when its destination point is
 * already finished, `active` when its destination is the courier's current target, and
 * `upcoming` otherwise.
 */
export function getRouteLegs(points: RoutePoint[]): RouteLeg[] {
  if (points.length < 2) return [];
  const activeIndex = getActivePointIndex(points);

  // The active leg is the one leading INTO the active point (targetIndex === activeIndex).
  // Special case: activeIndex === 0 means the courier is still heading to the very first
  // stop — there is no leg into it, so we highlight the first leg (0 → 1) as the imminent one.
  const activeLegTargetIndex = activeIndex <= 0 ? 1 : activeIndex;

  const legs: RouteLeg[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const targetIndex = i + 1;

    let state: RouteLeg['state'];
    if (targetIndex === activeLegTargetIndex) state = 'active';
    else if (targetIndex < activeLegTargetIndex) state = 'completed';
    else state = 'upcoming';

    legs.push({ from: points[i]!.coords, to: points[targetIndex]!.coords, state });
  }
  return legs;
}

/**
 * Same as {@link getRouteLegs} but anchored at the courier's live position. The courier
 * is physically at `courierCoords` and en route to the current active stop, so:
 *   - `courier → points[activeIndex]!` is the ACTIVE leg (this is the "courier → origin"
 *     segment the map was previously missing),
 *   - legs between already-visited stops (before the active stop) are `completed`,
 *   - legs from the active stop onward are `upcoming`.
 *
 * Falls back to {@link getRouteLegs} when the courier position is unavailable.
 */
export function getRouteLegsWithCourier(
  points: RoutePoint[],
  courierCoords: [number, number] | null | undefined,
): RouteLeg[] {
  if (!courierCoords || !isFiniteCoord(courierCoords[0], courierCoords[1]))
    return getRouteLegs(points);
  if (!points.length) return [];

  const activeIndex = getActivePointIndex(points);
  // getActivePointIndex only returns -1 for an empty route, already handled above.
  const target = activeIndex < 0 ? 0 : activeIndex;

  const legs: RouteLeg[] = [];

  // 1. Leg from the courier's live location to the stop they're currently heading to.
  legs.push({ from: courierCoords, to: points[target]!.coords, state: 'active' });

  // 2. Stop-to-stop legs: everything before the active stop is behind the courier
  //    (completed); everything from the active stop onward is still ahead (upcoming).
  for (let i = 0; i < points.length - 1; i += 1) {
    const targetIndex = i + 1;
    const state: RouteLeg['state'] = targetIndex <= target ? 'completed' : 'upcoming';
    legs.push({ from: points[i]!.coords, to: points[targetIndex]!.coords, state });
  }

  return legs;
}

/** YMaps3 bounds: [[minLong, minLat], [maxLong, maxLat]]. */
export type MapBounds = [[number, number], [number, number]];

/**
 * Padded bounding box that frames all given [long, lat] coords, so the map can fit the
 * whole route in view. Mirrors MapShow's 20% padding formula. Returns null for < 1 valid
 * coord; for a single coord, pads by a small fixed delta so the bounds are non-degenerate.
 */
export function getBoundsForCoords(coords: [number, number][]): MapBounds | null {
  const valid = coords.filter((c) => Array.isArray(c) && isFiniteCoord(c[0], c[1]));
  if (!valid.length) return null;

  const minLong = Math.min(...valid.map((c) => c[0]));
  const maxLong = Math.max(...valid.map((c) => c[0]));
  const minLat = Math.min(...valid.map((c) => c[1]));
  const maxLat = Math.max(...valid.map((c) => c[1]));

  // Fixed fallback padding when the span is zero (single point / identical coords).
  const longPadding = (maxLong - minLong) * 0.2 || 0.003;
  const latPadding = (maxLat - minLat) * 0.2 || 0.003;

  return [
    [minLong - longPadding, minLat - latPadding],
    [maxLong + longPadding, maxLat + latPadding],
  ];
}

/**
 * All [long, lat] coords for an order's route (origins + destinations), optionally with the
 * courier's live position prepended. Used to compute the map bounds when showing a route.
 */
export function getOrderRouteCoords(
  order: ActiveOrderLike | null | undefined,
  courierCoords?: [number, number] | null,
): [number, number][] {
  const points = getOrderRoutePoints(order).map((p) => p.coords);
  if (courierCoords && isFiniteCoord(courierCoords[0], courierCoords[1]))
    return [courierCoords, ...points];
  return points;
}
